import React, { useMemo } from 'react';
import { ROUTES } from '@/lib/constants';
import { createBrowserRouter, createMemoryRouter, Navigate } from 'react-router';
import lazyRetry from './lib/helpers/lazyRetry';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import GlobalContext from '@/context/GlobalContext';
import CrashSite from '@/components/ErrorBoundary/CrashSite';
import HypertrackerLayout from './components/Hypertracker/Layout';
import HyperTrackerGlobalContext from '@/context/HyperTrackerGlobalContext';

// Lazy-loaded components and pages
const LayoutNoSidebar = lazyRetry(
    () => import('./components/Layout/LayoutNoSidebar'),
    'layoutNoSidebar',
);
const PrivateRoute = lazyRetry(() => import('./components/PrivateRoute'), 'privateRoute');
const Layout = lazyRetry(() => import('@/components/Layout'), 'layout');
const LayoutOnlyContent = lazyRetry(
    () => import('@/components/Layout/LayoutOnlyContent'),
    'layoutOnlyContent',
);
const NotFound = lazyRetry(() => import('./pages/404'), 'notFound');
const Login = lazyRetry(() => import('./pages/Login'), 'login');
const Home = lazyRetry(() => import('./pages/Home'), 'home');
const HomePerformance = lazyRetry(() => import('./pages/HomePerformance'), 'homePerformance');
const HomeAnalytics = lazyRetry(() => import('./pages/HomeAnalytics'), 'homeAnalytics');
const HomeCalendar = lazyRetry(() => import('./pages/HomeCalendar'), 'homeCalendar');
const HomeReporting = lazyRetry(() => import('./pages/HomeReporting'), 'homeReporting');
const VerificationEmail = lazyRetry(() => import('./pages/Auth/VerifyEmail'), 'verifyEmail');
const ResetPassword = lazyRetry(
    () => import('./pages/ResetPassword/ResetPassword'),
    'resetPassword',
);
const ResetPasswordConfirm = lazyRetry(
    () => import('./pages/ResetPassword/ResetPasswordConfirm'),
    'resetPasswordConfirm',
);
const EmailTokenVerification = lazyRetry(
    () => import('./pages/Auth/EmailTokenVerification'),
    'emailTokenVerification',
);
const FailedAuth = lazyRetry(() => import('./pages/Auth/FailedAuth'), 'failedAuth');
const SsoPage = lazyRetry(() => import('./pages/Auth/Sso'), 'sso');
const UpgradePage = lazyRetry(() => import('./pages/Upgrade'), 'upgrade');
const Unlocked = lazyRetry(() => import('./pages/UnlockedPage'), 'unlocked');
const Journal = lazyRetry(() => import('./pages/Journal'), 'journal');
const JournalAdd = lazyRetry(() => import('./pages/JournalAdd'), 'journalAdd');
const JournalTrades = lazyRetry(() => import('./pages/JournalTrades'), 'journalTrades');
const JournalTradesAdd = lazyRetry(() => import('./pages/JournalTradesAdd'), 'journalTradesAdd');
const JournalEntry = lazyRetry(() => import('./pages/JournalEntry'), 'journalEntry');
const Verification = lazyRetry(() => import('./pages/Verification'), 'verification');
const Purchase = lazyRetry(() => import('./pages/Purchase'), 'purchase');
const ReceiptPage = lazyRetry(() => import('./pages/ReceiptPage'), 'receiptPage');
const ValidateTwitter = lazyRetry(() => import('./pages/ValidateTwitter'), 'validateTwitter');
const VerificationAdd = lazyRetry(() => import('./pages/VerificationAdd'), 'verificationAdd');
const VerificationEdit = lazyRetry(() => import('./pages/VerificationEdit'), 'verificationEdit');
const SocialLogin = lazyRetry(() => import('./pages/SocialLogin'), 'socialLoginPage');
const SocialRegister = lazyRetry(() => import('./pages/Auth/SocialRegister'), 'socialRegisterPage');
const Leaderboard = lazyRetry(() => import('./pages/Leaderboard'), 'leaderboardPage');
const NotificationsPage = lazyRetry(() => import('./pages/NotificationsPage'), 'notificationsPage');
const ApiManager = lazyRetry(() => import('./pages/ApiManager'), 'apiManager');
const AddApiManager = lazyRetry(() => import('./pages/AddApiManager'), 'addApiManager');
const RequestNewExchange = lazyRetry(
    () => import('./pages/RequestNewExchange'),
    'requestNewExchange',
);
const Settings = lazyRetry(() => import('./pages/Settings'), 'settings');
const StoragePage = lazyRetry(() => import('./pages/Storage'), 'storage');
const AccountPage = lazyRetry(() => import('./pages/Account'), 'account');

const HypertrackerSegment = lazyRetry(
    () => import('./pages/HypertrackerSegment'),
    'hypertrackerSegments',
);
const HypertrackerWallet = lazyRetry(
    () => import('./pages/HypertrackerWallet'),
    'hypertrackerWallet',
);
const HypertrackerHeatmap = lazyRetry(
    () => import('./pages/HypertrackerHeatmap'),
    'hypertrackerHeatmap',
);
const HypertrackerFavourites = lazyRetry(
    () => import('./pages/HypertrackerFavorites'),
    'hypertrackerFavourites',
);
const HypertrackerStats = lazyRetry(() => import('./pages/HypertrackerStats'), 'HypertrackerStats');

const HypertrackerHome = lazyRetry(() => import('./pages/HypertrackerHome'), 'hypertrackerHome');
const HypertrackerConnections = lazyRetry(
    () => import('./pages/HypertrackerConnections'),
    'hypertrackerConnections',
);
const HypertrackerPerps = lazyRetry(() => import('./pages/HypertrackerPerps'), 'hypertrackerPerps');
const HypertrackerPerpsLanding = lazyRetry(
    () => import('./pages/HypertrackerPerpsLanding'),
    'hypertrackerPerpsLanding',
);
const HypertrackerBuilder = lazyRetry(
    () => import('./pages/HypertrackerBuilder'),
    'hypertrackerBuilder',
);
const HypertrackerBuilderPersonal = lazyRetry(
    () => import('./pages/HypertrackerBuilderPersonal'),
    'hypertrackerBuilderPersonal',
);
const HypertrackerLeaderboards = lazyRetry(
    () => import('./pages/HypertrackerLeaderboards'),
    'hypertrackerLeaderboards',
);
const HypertrackerAlerts = lazyRetry(
    () => import('./pages/HypertrackerAlerts'),
    'hypertrackerAlerts',
);

const HypertrackerMarketRadar = lazyRetry(
    () => import('./pages/HypertrackerMarketRadar'),
    'hypertrackerMarketRadar',
);

const HypertrackerFlows = lazyRetry(
    () => import('./pages/HypertrackerFlows/HypertrackerFlows'),
    'hypertrackerFlows',
);

const HypertrackerLiquidations = lazyRetry(
    () => import('./pages/HypertrackerLiquidations'),
    'hypertrackerLiquidations',
);

const HypertrackerHipPage = lazyRetry(() => import('./pages/HypertrackerHIP'), 'hypertrackerHip');

const HypertrackerApi = lazyRetry(() => import('./pages/HypertrackerApi'), 'hypertrackerApi');
const HypertrackerPricing = lazyRetry(
    () => import('./pages/HypertrackerPricing'),
    'hypertrackerPricing',
);

const HypertrackerApiDashboard = lazyRetry(
    () => import('./pages/HypertrackerApiDashboard'),
    'hypertrackerApiDashboard',
);
const HypertrackerPurchase = lazyRetry(
    () => import('./pages/HypertrackerPurchase'),
    'hypertrackerPurchase',
);

const HypertrackerOpenTesla = lazyRetry(
    () => import('./pages/HypertrackerOpenTesla'),
    'hypertrackerOpenTela',
);

const HypertrackerAlphafeed = lazyRetry(
    () => import('./pages/HypertrackerAlphafeed/Alphafeed'),
    'hypertrackerAlphafeed',
);

const HypertrackerAlphafeedPlaceholder = lazyRetry(
    () => import('./pages/HypertrackerAlphafeed/HypertrackerAlphafeedPlaceholder'),
    'hypertrackerAlphafeedPlaceholder',
);

const RootRedirect: React.FC = () => {
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    if (member.id !== null && !member.verified) {
        return <Navigate to={ROUTES.VERIFY_EMAIL} />;
    }
    if (member.id !== null && member.verified) {
        return <Navigate to={ROUTES.HOME} />;
    }
    return <Navigate to={ROUTES.LOGIN} />;
};

const LoginRoute: React.FC = () => {
    const member = useSelector((state: RootState) => selectMemberProfile(state));
    return member.id !== null ? <Navigate to={ROUTES.HOME} /> : <Login />;
};

const UpgradeLayout: React.FC = () => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down('md'));
    return <LayoutNoSidebar withoutHeader={isTabletScreen} />;
};

const routes = [
    {
        path: '',
        element: <GlobalContext />,
        errorElement: <CrashSite />,
        children: [
            { path: '*', element: <NotFound /> },
            {
                path: '',
                element: <RootRedirect />,
            },
            { path: ROUTES.SSO, element: <SsoPage /> },
            {
                path: `${ROUTES.EMAIL_TOKEN_VERIFICATION}/:token`,
                element: <EmailTokenVerification />,
            },
            { path: ROUTES.FAILED_AUTH, element: <FailedAuth /> },
            {
                path: `${ROUTES.RESET_PASSWORD_CONFIRM}/:token`,
                element: <ResetPasswordConfirm />,
            },
            {
                path: ROUTES.VERIFY_EMAIL,
                element: <VerificationEmail />,
            },
            { path: ROUTES.RESET_PASSWORD, element: <ResetPassword /> },
            {
                path: '/social',
                children: [
                    { path: 'login', element: <SocialLogin /> },
                    { path: 'registration', element: <SocialRegister /> },
                ],
            },
            {
                path: ROUTES.HOME,
                element: (
                    <PrivateRoute>
                        <Layout />
                    </PrivateRoute>
                ),
                children: [
                    { path: '', element: <Home /> },
                    { path: 'performance', element: <HomePerformance /> },
                    { path: 'analytics', element: <HomeAnalytics /> },
                    { path: 'calendar', element: <HomeCalendar /> },
                    { path: 'reporting', element: <HomeReporting /> },
                    { path: '*', element: <NotFound /> },
                    { path: 'verification', element: <Verification /> },
                    {
                        path: 'journal',
                        children: [
                            { path: '', element: <Journal /> },
                            { path: 'add', element: <JournalAdd /> },
                            { path: ':journalId', element: <JournalTrades /> },
                            { path: ':journalId/add', element: <JournalTradesAdd /> },
                            { path: ':journalId/entry', element: <JournalEntry /> },
                        ],
                    },

                    {
                        path: 'api-manager',
                        children: [
                            {
                                path: '',
                                element: <ApiManager />,
                            },
                            {
                                path: 'add',
                                children: [
                                    {
                                        path: '',
                                        element: <AddApiManager />,
                                    },
                                    {
                                        path: 'request-new-exchange',
                                        element: <RequestNewExchange />,
                                    },
                                ],
                            },
                            {
                                path: ':exchange',
                                children: [
                                    {
                                        path: '',
                                        element: <ApiManager />,
                                    },
                                    {
                                        path: ':accountId',
                                        element: <ApiManager />,
                                    },
                                ],
                            },
                        ],
                    },
                    { path: 'storage', element: <StoragePage /> },
                    { path: 'accounts/:exchange/:accountId', element: <AccountPage /> },
                    { path: 'settings', element: <Settings /> },
                    { path: 'notifications', element: <NotificationsPage /> },
                    { path: 'leaderboard', element: <Leaderboard /> },
                    // { path: 'intelligence', element: <Intelligence /> },
                ],
            },
            {
                path: ROUTES.LOGIN,
                element: <LoginRoute />,
            },
            {
                path: 'upgrade',
                element: (
                    <PrivateRoute>
                        <UpgradeLayout />
                    </PrivateRoute>
                ),
                children: [
                    { path: '*', element: <NotFound /> },
                    {
                        path: '',
                        element: <UpgradePage />,
                    },
                    { path: 'unlocked', element: <Unlocked /> },
                ],
            },
            {
                path: '',
                element: (
                    <PrivateRoute>
                        <LayoutOnlyContent />
                    </PrivateRoute>
                ),
                children: [
                    { path: 'purchase', element: <Purchase /> },
                    {
                        path: ROUTES.PAYPAL_SUCCESS,
                        element: <Purchase />,
                    },
                    {
                        path: ROUTES.STRIPE_SUCCESS,
                        element: <Purchase />,
                    },
                    {
                        path: ROUTES.STRIPE_CANCEL,
                        element: <Purchase />,
                    },
                    {
                        path: ROUTES.CP_SUCCESS,
                        element: <Purchase />,
                    },
                    {
                        path: ROUTES.CP_CANCEL,
                        element: <Purchase />,
                    },
                    {
                        path: `${ROUTES.RECEIPT}/:orderId`,
                        element: <ReceiptPage />,
                    },
                ],
            },
            {
                path: ROUTES.VERIFICATION_ADD,
                element: (
                    <PrivateRoute>
                        <LayoutNoSidebar />
                    </PrivateRoute>
                ),
                children: [{ path: '', element: <VerificationAdd /> }],
            },
            {
                path: ROUTES.VERIFICATION_EDIT,
                element: (
                    <PrivateRoute>
                        <LayoutNoSidebar />
                    </PrivateRoute>
                ),
                children: [{ path: '', element: <VerificationEdit /> }],
            },
            {
                path: ROUTES.VALIDATE_TWITTER,
                element: (
                    <PrivateRoute>
                        <ValidateTwitter />
                    </PrivateRoute>
                ),
            },
        ],
    },

    {
        path: ROUTES.HYPERTRACKER,
        element: <HyperTrackerGlobalContext />,
        errorElement: <CrashSite hypertracker />,

        children: [
            {
                path: `${ROUTES.HYPERTRACKER_PURCHASE}`,
                element: <HypertrackerPurchase />,
                errorElement: <CrashSite hypertracker />,
            },
            {
                path: '',
                element: <HypertrackerLayout />,
                errorElement: <CrashSite hypertracker />,
                children: [
                    { path: '', element: <HypertrackerHome /> },
                    { path: '*', element: <NotFound /> },
                    {
                        path: `${ROUTES.HYPERTRACKER_SEGMENTS}/:segment`,
                        element: <HypertrackerSegment />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_WALLET}/:wallet`,
                        element: <HypertrackerWallet />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_HEATMAP}`,
                        element: <HypertrackerHeatmap />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_FAVORITES}`,
                        element: <HypertrackerFavourites />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_CONNECTIONS}`,
                        element: <HypertrackerConnections />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_PERPS}`,
                        element: <HypertrackerPerpsLanding />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_PERPS}/:coin`,
                        element: <HypertrackerPerps />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_OPEN_TESLA}`,
                        element: <HypertrackerOpenTesla />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_STATS}`,
                        element: <HypertrackerStats />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_WALLET}`,
                        element: <Navigate to={`${ROUTES.HYPERTRACKER}`} replace />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_SEGMENTS}`,
                        element: <Navigate to={`${ROUTES.HYPERTRACKER}`} replace />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_BUILDER}`,
                        element: <HypertrackerBuilder />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}/:builder`,
                        element: <HypertrackerBuilderPersonal />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_BUILDER_PERSONAL}`,
                        element: <Navigate to={`${ROUTES.HYPERTRACKER}`} replace />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_LEADERBOARDS}`,
                        element: <HypertrackerLeaderboards />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_LEADERBOARDS}/:leaderboard`,
                        element: <HypertrackerLeaderboards />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_API}`,
                        element: <HypertrackerApi />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_ALERTS}`,
                        // element: <HypertrackerAlertsLanding />,
                        element: <HypertrackerAlerts />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_PRICING}`,
                        element: <HypertrackerPricing />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_MARKET_RADAR}`,
                        element: <HypertrackerMarketRadar />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_FLOWS}`,
                        element: <HypertrackerFlows />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_LIQUIDATIONS}`,
                        element: <HypertrackerLiquidations />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_HIP}`,
                        element: <HypertrackerHipPage />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_API_DASHBOARD}`,
                        element: <HypertrackerApiDashboard />,
                    },
                    {
                        path: `${ROUTES.HYPERTRACKER_ALPHAFEED}`,
                        element: <HypertrackerAlphafeed />,
                    },
                ],
            },
        ],
    },
];

const useAppRouter = () => {
    return useMemo(() => {
        const isEmbedded = typeof window !== 'undefined' && !!window?.__CMM_WIDGET__;
        const initialPath = window?.__CMM_WIDGET__?.initialPath || '/';
        return isEmbedded
            ? createMemoryRouter(routes, { initialEntries: [initialPath] })
            : createBrowserRouter(routes);
    }, []);
};

export default useAppRouter;
